import React from 'react';
import { motion, useTransform } from 'framer-motion';
import './ProgramStoryScene.css';

const agents = [
  { name: 'Atlas', task: 'Research context', image: 'atlas-comp-bio.png', width: 602, height: 435 },
  { name: 'Orin', task: 'Assay design', image: 'orin-protocol.png', width: 256, height: 256 },
  { name: 'Sigma', task: 'Interpretation', image: 'sigma-stats.png', width: 500, height: 500 },
];

function BiologyMark() {
  return <svg className="story-biology-mark" viewBox="0 0 200 240" fill="none" aria-hidden="true">
    <path className="story-biology-underlay" d="M100 211V132L48 78 25 27M100 132l52-54 23-51" />
    <path className="story-biology-backbone" d="M100 211V132L48 78 25 27M100 132l52-54 23-51" />
    <path className="story-biology-detail" d="m84 123-48-50-17-29m97 79 48-50 17-29M89 159v37m22-37v37" />
    <circle cx="25" cy="27" r="5" /><circle cx="175" cy="27" r="5" />
  </svg>;
}

function Scene({ poses, chapter }) {
  return <div className="program-story-scene" data-static-chapter={chapter} aria-hidden="true">
    <motion.div className="story-biological-field" style={{ opacity: poses.contextOpacity, scale: poses.contextScale }}>
      <div className="story-field-ring" />
      <div className="story-research-fragment story-research-history">
        <span>Research history</span><i /><i /><i />
      </div>
      <div className="story-research-fragment story-research-biology">
        <span>Partner biology</span><i /><i />
      </div>
      <div className="story-research-fragment story-research-assays">
        <span>Assay history</span><i /><i /><i />
      </div>
    </motion.div>

    <motion.div className="story-continuing-material" style={{
      x: poses.materialX, y: poses.materialY, scale: poses.materialScale,
      rotate: poses.materialRotate, opacity: poses.materialOpacity,
    }}>
      <BiologyMark />
      <motion.div className="story-working-notes" style={{ opacity: poses.notesOpacity }}>
        <span>Binding</span><span>Function</span>
        <div className="story-assay-wells">
          {Array.from({ length: 18 }, (_, index) => <i key={index} />)}
        </div>
      </motion.div>
    </motion.div>

    <motion.div className="story-agent-ensemble" style={{ opacity: poses.agentOpacity, y: poses.agentY, scale: poses.agentScale }}>
      {agents.map((agent, index) => <div key={agent.name} className={'story-agent story-agent-' + index}>
        <div className="story-agent-portrait">
          <img src={'/images/discovery/agents/' + agent.image} alt="" width={agent.width} height={agent.height} loading="lazy" decoding="async" />
        </div>
        <strong>{agent.name}</strong><span>{agent.task}</span>
      </div>)}
    </motion.div>

    <motion.div className="story-program-arrival" style={{ opacity: poses.programOpacity, y: poses.programY, scale: poses.programScale }}>
      <strong className="story-program-name">CCP-001</strong>
      <span className="story-program-partners">Partner + Cornucopia</span>
    </motion.div>

    <motion.div className="story-harness-signature" style={{ opacity: poses.signatureOpacity, y: poses.signatureY }}>
      <img src="/brand/cornucopia-mark.svg" alt="" width="37" height="42" />
      <span>Cornucopia</span>
    </motion.div>
  </div>;
}

function MovingScene({ progress }) {
  const contextOpacity = useTransform(progress, [0, .18, .28, 1], [1, 1, 0, 0]);
  const contextScale = useTransform(progress, [0, .18, .28, 1], [1, 1, .88, .88]);
  const materialX = useTransform(progress, [0, .18, .4, .48, .58, .65, .84, 1], [0, 0, 0, -70, 70, 0, 0, 0]);
  const materialY = useTransform(progress, [0, .18, .4, .48, .58, .65, .84, 1], [0, 0, -16, 45, 45, 20, -38, -38]);
  const materialScale = useTransform(progress, [0, .18, .4, .65, .84, 1], [1, 1, .66, .66, .28, .28]);
  const materialRotate = useTransform(progress, [0, .18, .4, .48, .58, .65, .84, 1], [-9, -9, 0, -5, 5, 0, 9, 9]);
  const materialOpacity = useTransform(progress, [0, .65, .77, 1], [1, 1, 0, 0]);
  const notesOpacity = useTransform(progress, [0, .24, .42, .6, .78, 1], [0, 0, 1, 1, 0, 0]);
  const agentOpacity = useTransform(progress, [0, .28, .42, .62, .72, 1], [0, 0, 1, 1, 0, 0]);
  const agentY = useTransform(progress, [0, .28, .42, .62, .74, 1], [38, 38, 0, 0, -34, -34]);
  const agentScale = useTransform(progress, [0, .28, .42, .62, .74, 1], [.9, .9, 1, 1, .9, .9]);
  const programOpacity = useTransform(progress, [0, .72, .84, 1], [0, 0, 1, 1]);
  const programY = useTransform(progress, [0, .72, .84, 1], [38, 38, 0, 0]);
  const programScale = useTransform(progress, [0, .72, .84, 1], [.94, .94, 1, 1]);
  const signatureOpacity = useTransform(progress, [0, .22, .42, 1], [0, 0, 1, 1]);
  const signatureY = useTransform(progress, [0, .22, .42, 1], [16, 16, 0, 0]);
  return <Scene poses={{ contextOpacity, contextScale, materialX, materialY, materialScale,
    materialRotate, materialOpacity, notesOpacity, agentOpacity, agentY, agentScale,
    programOpacity, programY, programScale, signatureOpacity, signatureY }} />;
}

const stills = [
  { contextOpacity: 1, contextScale: 1, materialX: 0, materialY: 0, materialScale: 1, materialRotate: -9, materialOpacity: 1,
    notesOpacity: 0, agentOpacity: 0, agentY: 0, agentScale: 1, programOpacity: 0, programY: 0, programScale: 1, signatureOpacity: 0, signatureY: 0 },
  { contextOpacity: 0, contextScale: 1, materialX: 0, materialY: 30, materialScale: .66, materialRotate: 0, materialOpacity: 1,
    notesOpacity: 1, agentOpacity: 1, agentY: 0, agentScale: 1, programOpacity: 0, programY: 0, programScale: 1, signatureOpacity: 1, signatureY: 0 },
  { contextOpacity: 0, contextScale: 1, materialX: 0, materialY: 0, materialScale: .28, materialRotate: 0, materialOpacity: 0,
    notesOpacity: 0, agentOpacity: 0, agentY: 0, agentScale: 1, programOpacity: 1, programY: 0, programScale: 1, signatureOpacity: 1, signatureY: 0 },
];

export default function ProgramStoryScene({ progress, chapter = 0 }) {
  return progress ? <MovingScene progress={progress} /> : <Scene poses={stills[chapter] || stills[0]} chapter={chapter} />;
}
